import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  Autocomplete,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearJefedeProductos = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // ==========================================================================
  // PASO 1: MANEJO DE ESTADO (State)
  // Se guarda el ID (usrcodigo) como string vacío por defecto
  // ==========================================================================
  const [formData, setFormData] = useState({
    jefecodigo: "",
    jefedescri: "",
    jefestatus: "A",
    jefecomisiona: false,
    usrcodigo: "",
    ciacodigo2: "",
    emcodemp: "",
  })

  // ==========================================================================
  // PASO 2: EXTRACCIÓN DEFENSIVA EN REACT QUERY
  // ==========================================================================
  const { data: rawUsuarios, isLoading: isLoadingUsuarios } = useQuery({
    queryKey: ["listaUsuarios_Crear"],
    queryFn: async () => {
      try {
        const response = await api.get("/JefedeProductos/getUsuariosActivos")

        // Extracción robusta para garantizar siempre un arreglo puro []
        const rawData = response?.data?.data || response?.data || response || []
        const listaValidada = Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []

        return listaValidada
      } catch (error) {
        console.error("Error al cargar Usuarios:", error)
        return []
      }
    },
  })

  // Formateamos la lista validada para el Autocomplete
  const listaUsuarios = (rawUsuarios || []).map((item) => ({
    id: item.usrcodigo || item.id || "",
    label: item.label || `${item.usrcodigo || item.id || ""} - ${item.usrnombre || item.descri || ""}`.trim(),
  }))

  const {
    mutateAsync: SaveCreacionJefe,
    isPending: isSavingCreacion,
    isError,
  } = useMutation({
    queryKey: ["isCreatingJefeProducto"],
    fn: async (data) => {
      // Ajustamos el checkbox a entero (1/0) para el backend
      const payload = {
        ...data,
        jefecomisiona: data.jefecomisiona ? 1 : 0,
      }
      const response = await api.post("/JefedeProductos/createJefedeProductos", payload)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    let finalValue = value.toUpperCase()

    // Respetando las longitudes de la base de datos
    if (field === "jefecodigo") finalValue = finalValue.slice(0, 6)
    else if (field === "jefedescri") finalValue = finalValue.slice(0, 80)
    else if (field === "ciacodigo2") finalValue = finalValue.slice(0, 2)
    else if (field === "emcodemp") finalValue = finalValue.slice(0, 10)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleCheckboxChange = (field, checked) => {
    setFormData((prev) => ({ ...prev, [field]: checked }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Validaciones del frontend
    if (!formData.jefecodigo.trim()) return showWarning("El Código del Jefe de Producto es obligatorio")
    if (!formData.jefedescri.trim()) return showWarning("La Descripción del Jefe de Producto es obligatoria")

    await SaveCreacionJefe(formData)
  }

  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  const toolbarActions = guardarAction
    ? [
        {
          label: guardarAction.acccaption,
          key: guardarAction.acccaption,
          icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
        },
      ]
    : [{ label: "Guardar", key: "GUARDAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingCreacion || isLoadingUsuarios}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Nuevo Jefe de Producto</b>
        </div>

        <CustomBackdrop isLoading={isSavingCreacion || isLoadingUsuarios} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Jefe de Producto
            </Typography>

            <Grid container spacing={3}>
              {/* CÓDIGO */}
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código Jefe *"
                  value={formData.jefecodigo}
                  onChange={(e) => handleInputChange("jefecodigo", e.target.value)}
                  error={isError && !formData.jefecodigo}
                  inputProps={{ maxLength: 6 }}
                />
              </Grid>

              {/* DESCRIPCIÓN */}
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.jefedescri}
                  onChange={(e) => handleInputChange("jefedescri", e.target.value)}
                  error={isError && !formData.jefedescri}
                  inputProps={{ maxLength: 80 }}
                />
              </Grid>

              {/* ESTADO */}
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  label="Estado *"
                  value={formData.jefestatus}
                  onChange={(e) => setFormData((prev) => ({ ...prev, jefestatus: e.target.value }))}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>

              {/* ==========================================================================
                  PASO 3: CONFIGURACIÓN ESTRICTA DEL COMPONENTE <Autocomplete>
              ========================================================================== */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  // 1. options: Nunca pasar undefined
                  options={listaUsuarios}
                  // 2. getOptionLabel: Manejar casos nulos o sin propiedad
                  getOptionLabel={(option) => option.label || ""}
                  // 3. value: Vincular con estado y fallback a null
                  value={listaUsuarios.find((c) => c.id === formData.usrcodigo) || null}
                  // 4. isOptionEqualToValue: OBLIGATORIO comparar por llave primaria
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  // 5. onChange: Actualizar el estado de forma segura (PASO 4 implícito si hay limpieza)
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      usrcodigo: newValue ? newValue.id : "",
                    }))
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Usuario Asignado"
                      placeholder="Seleccione un usuario..."
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              {/* CÓDIGO COMPAÑÍA 2 */}
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Cía 2"
                  value={formData.ciacodigo2}
                  onChange={(e) => handleInputChange("ciacodigo2", e.target.value)}
                  inputProps={{ maxLength: 2 }}
                />
              </Grid>

              {/* CÓDIGO EMPLEADO */}
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Cód. Empleado"
                  value={formData.emcodemp}
                  onChange={(e) => handleInputChange("emcodemp", e.target.value)}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              {/* CHECKBOX COMISIONA */}
              <Grid item xs={12} sm={3} sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.jefecomisiona}
                      onChange={(e) => handleCheckboxChange("jefecomisiona", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="¿Comisiona?"
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearJefedeProductos
